<template>
<div>
  <div v-if="loading || localLoading" class="file-loading">
    <i class="fa fa-refresh fa-spin fa-2x text-muted"></i>
  </div>

  <div v-if="!rule && !loading" class="no-file-container" id="rulesEditArea-noFileContainer">
    <h5>{{ 'SELECT_FILE' | translate }}</h5>
  </div>

  <template v-else-if="rule">
    <div class="row mb-3">
      <div class="col">
        <h5 v-if="!newRule">
          {{ rule.filename }}
          <button class="btn btn-sm btn-light ml-2" @click="resetRule">
            <i class="fa fa-refresh"></i>
          </button>
        </h5>
        <div v-else class="input-group">
          <input type="text" v-model="newRuleFilename" :placeholder="'FILENAME' | translate"
                 class="form-control" :class="{ 'invalid': newRuleFilenameIllegal }">
          <div class="input-group-append">
            <div class="input-group-text">.rdslr</div>
          </div>
        </div>
      </div>
      <div class="col">
        <div v-if="newRule && newRuleFilenameIllegal" class="alert alert-danger filename-alert mb-0">
          <i class="fa fa-exclamation-circle"></i> {{ 'ALLOWED_SYMBOLS' | translate }}
        </div>
      </div>
      <div class="col-4 text-right">
        <div class="btn-group" role="group">
          <span v-tooltip.bottom-start="hasValidationError ? $options.filters.translate('CHECK_VALIDATION_ERRORS') : '' ">
            <button type="button" class="btn btn-sm btn-success" @click="saveRule"
                    v-bind:disabled="hasValidationError || (newRule && (!newRuleFilename || newRuleFilenameIllegal))">
              <span v-if="!saveLoading">{{ 'SAVE' | translate }}</span>
              <span v-else><i class="fa fa-refresh fa-spin"></i> {{ 'SAVING' | translate }} </span>
            </button>
          </span>
          <div class="btn btn-sm btn-default checkbox-inside">
            <div class="custom-control alternative custom-checkbox">
              <input type="checkbox" class="custom-control-input" id="checkbox_ruleEnabled" v-model="isRuleEnabled">
              <label class="custom-control-label" for="checkbox_ruleEnabled">{{ "ENABLED" | translate }}</label>
            </div>
          </div>
          <button type="button" class="btn btn-sm btn-default" @click="confirmRuleRemoval">
            <span v-if="!deleteLoading">{{ 'DELETE' | translate }}</span>
            <span v-else class="text-danger"><i class="fa fa-refresh fa-spin"></i> {{ 'DELETING' | translate }} </span>
          </button>
        </div>
      </div>
    </div>
    <table class="table table-sm table-striped table-hover">
      <template v-for="(clauses, clauseType) in ruleClauses">
        <thead>
          <tr>
            <td width="30" class="pl-2">
              <span class="action-icon text-success" @click="showRuleAddModal(clauseType, -1)">
                <i class="fa fa-plus-square"></i>
              </span>
            </td>
            <td colspan="2" class="font-weight-bold">
              {{ clauseType.toUpperCase() | translate }}
            </td>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(clauseElements, clauseIndex) in clauses"> <!-- clauseIndex is zero based -->
            <td width="30" class="pl-2">
              <span class="action-icon text-success" @click="showRuleAddModal(clauseType, clauseIndex)">
                <i class="fa fa-plus-square"></i>
              </span>
            </td>
            <td v-if="typeof clauseElements === 'string'" class="with-counter">{{ clauseElements }}</td>
            <td v-else class="with-counter">
              <template v-for="el in clauseElements">
                <span v-if="!el.startsWith('$input-')">{{ el }}</span>
                <template v-else >
                  <input type="text" v-model="clauseInputs[el].value"
                         v-bind:disabled="saveLoading"
                         :class="{ 'invalid': clauseInputValidations[el] }"
                         v-tooltip.top-start="clauseInputValidations[el] ? $options.filters.translate('INPUT_VALIDATION_ERROR') + ' ' + clauseInputs[el].validationRegex : '' "
                         class="form-control form-control-sm">
                </template>
              </template>
            </td>
            <td width="30" class="text-right pr-2">
              <span class="action-icon text-danger" @click="removeClauseRow(clauseType, clauseIndex)">
                <i class="fa fa-minus-square"></i>
              </span>
            </td>
          </tr>
        </tbody>
      </template>
    </table>
  </template>

  <vodal :show="ruleAddModalShown"
         :closeOnEsc="true"
         :closeOnClickMask="false"
         :customStyles="{'top': '80px', 'bottom': 'auto', 'height': '80%'}"
         @hide="hideRuleAddModal">
    <rule-add-modal :selectedClause="ruleAddModalClause"
                    :selectedIndex="lastSelectedPostion"
                    :dslService="dslService"
                    @hide-rule-add="hideRuleAddModal"
                    @add-clause-row="addClauseRow"
                    @selection-modal-index="setSelectedRuleModalIndex">
    </rule-add-modal>
  </vodal>
  <confirm-dialog :show="showConfirmForRemoveRule"
                  :textLabel="'msg.SURE_DELETE_FILE'"
                  @hide-dialog="showConfirmForRemoveRule = false"
                  @confirm="removeRule">
  </confirm-dialog>
</div>
</template>

<script>
import RuleAddModal from './RuleAddModal';
import ConfirmDialog from '../../dialog/ConfirmDialog';
import Vodal from 'vodal';
import DslService from '../../../service/DslService';
import RestService from '../../../service/RestService';

export default {
  props: ['rule', 'newRule', 'loading'],
  components: { RuleAddModal, ConfirmDialog, Vodal },
  data: function () {
    return {
      dslService: this.initDslService(),
      rest: this.initRestService(),
      ruleClauses: null,
      clauseInputs: {},
      ruleAddModalShown: false,
      ruleAddModalClause: null,
      rulePosition: null,
      newRuleFilename: null,
      newRuleFilenameIllegal: false,
      inputCounter: 0,
      localLoading: false,
      saveLoading: false,
      deleteLoading: false,
      showConfirmForRemoveRule: false,
      isRuleEnabled: true,
      enabledFalseOption: '\tenabled false\n',
      ruleDefaultContent: 'package nl.paston.apollon.rules;\n\n' +
                          'rule "$filename$"\n' +
                          '\twhen\n' +
                          '\tthen\n' +
                          'end\n',
      lastModalRuleSelectionIndex: {
        'when': 0,
        'then': 0
      },
      enabledOptionIdentifier: '\tenabled false',
      packageIdentifier: 'package nl.paston',
      whenIdentifier: '[\r\n]+\twhen',
      thenIdentifier: '[\r\n]+\tthen',
      endIdentifier: '[\r\n]+end'
    }
  },
  beforeMount: function() {
    this.dslService.initDslRegExp();
  },
  computed: {
    originalRuleClauses: function() {
      let clauses = {};
      clauses['when'] = this.extractRuleClauseToArray(this.whenIdentifier, this.thenIdentifier);
      clauses['then'] = this.extractRuleClauseToArray(this.thenIdentifier, this.endIdentifier);
      return clauses;
    },
    stringifiedRuleClauses: function() {
      let stringClauses = {};
      stringClauses['when'] = this.convertRuleClauseToString('when');
      stringClauses['then'] = this.convertRuleClauseToString('then');
      return stringClauses;
    },
    lastSelectedPostion: function() {
      return this.lastModalRuleSelectionIndex[this.ruleAddModalClause];
    },
    clauseInputValidations: function() {
      let validations = {};
      for (let inputName in this.clauseInputs) {
        if (!this.clauseInputs.hasOwnProperty(inputName)) continue;
        let inputValue = this.clauseInputs[inputName].value;
        let validationRegex = this.clauseInputs[inputName].validationRegex;
        validations[inputName] = inputValue !== inputValue.match(validationRegex)[0]
      }
      return validations;
    },
    hasValidationError: function () {
      let hasValidationError = false;
      for (let inputName in this.clauseInputs) {
        if (!this.clauseInputs.hasOwnProperty(inputName)) continue;
        let inputValue = this.clauseInputs[inputName].value;
        let validationRegex = this.clauseInputs[inputName].validationRegex;
        if (inputValue !== inputValue.match(validationRegex)[0]) {
          hasValidationError = true;
          break;
        }
      }
      return hasValidationError;
    }
  },
  methods: {
    initDslService() {
      return new DslService();
    },
    initRestService() {
      return new RestService();
    },
    enableClauseRowEditModeIfApplicable(clauseType, row) {
      let exactRdslrClause = this.ruleClauses[clauseType][row];
      if (typeof exactRdslrClause !== 'string') {
        this.$log.info('ERROR: Some error occurred - the currentClause is not a string. Is it already in edit mode?');
        return;
      }

      let replacementFunction = this.dslService.getReplacementFunctionForRdslrClause(clauseType, exactRdslrClause);
      if (replacementFunction) {
        let replacedClause = replacementFunction(exactRdslrClause, this.setClauseInputRowAndIncrementTheCounter, this.getInputCounter);
        this.ruleClauses[clauseType][row] = replacedClause; // this is replacing the actual text row with the clause split into static and variable parts.
      } else {
        // No changes are needed on this row.
        this.$log.info('INFO: Replacement function for Rdslr clause was not found. No dsl regex was matched for: ' + exactRdslrClause);
      }
    },
    getInputCounter() {
      return this.inputCounter;
    },
    setClauseInputRowWithEmptyValueAndIncrementTheCounter(key, value, validationRegex) {
      this.setClauseInputRowAndIncrementTheCounter(key, value, validationRegex, true);
    },
    setClauseInputRowAndIncrementTheCounter(key, value, validationRegex, setTheValueEmpty) {
      validationRegex = validationRegex || '.*'; // if there is no validation regex, then accept everything.
      value = setTheValueEmpty ? '' : value;
      this.setClauseInput(key, {value: value, validationRegex: validationRegex});
      this.inputCounter++; // increment and then return the number
    },

    enableClauseEditMode() {
      let clausetype = 'when';
      for (let i = 0; i < this.ruleClauses[clausetype].length; i++) {
        this.enableClauseRowEditModeIfApplicable(clausetype, i);
      }
      clausetype = 'then';
      for (let i = 0; i < this.ruleClauses[clausetype].length; i++) {
        this.enableClauseRowEditModeIfApplicable(clausetype, i);
      }
    },

    extractRuleClauseToArray(startAfter, endBefore) {
      let ruleClauseArray = [];
      if (this.rule && this.rule.content !== '') {
        let clause = this.getSubstringBetween(this.rule.content, startAfter, endBefore);
        ruleClauseArray = clause ? clause.split(new RegExp('[\\r\\n]')) : [];
        ruleClauseArray = ruleClauseArray.map(row => row.trim()).filter(row => row !== '');
      }
      return ruleClauseArray;
    },

    getSubstringBetween (wholeString, startAfter, endBefore) {
      let inBetweenRegexp = new RegExp('(?<=' + startAfter + ')([\\s\\S]*)(?=' + endBefore + ')');
      return (wholeString.match(inBetweenRegexp) ? wholeString.match(inBetweenRegexp)[1] : null);
    },

    convertRuleClauseToString(clauseType) {
      return this.ruleClauses[clauseType].map(clauseItem =>
        (typeof clauseItem !== 'string')
          ? clauseItem.map(item => item.startsWith('$input-') ? this.clauseInputs[item].value : item).join('') : clauseItem
      );
    },
    resetRule() {
      if (JSON.stringify(this.ruleClauses) !== JSON.stringify(this.originalRuleClauses)) {
        this.localLoading = true;
        setTimeout(() => { this.localLoading = false; }, 200); // to make sure the loader is going to show for atleast 200ms
        this.ruleClauses = JSON.parse(JSON.stringify(this.originalRuleClauses)); // cloning it
        this.enableClauseEditMode(); // make all the rdslr parameter fields editable
        this.setRuleEnabled(); // sets this.isRuleEnabled for the newly loaded or reset rule
        this.$emit('reset-changes');
      }
    },
    setRuleEnabled() {
      if (this.rule && this.rule.content) {
        let contentBeforeWhen = this.getSubstringBetween(this.rule.content, this.packageIdentifier, this.whenIdentifier);
        this.isRuleEnabled = !new RegExp(this.enabledOptionIdentifier).test(contentBeforeWhen);
      }
    },
    addClauseRow(rowToAdd) {
      this.addClauseRowAndItsValidations(rowToAdd);
      this.hideRuleAddModal();
      this.$emit('made-changes');
    },

    addClauseRowAndItsValidations(rowToAdd) {
      // This function is needed when adding a new DSL from the popup to split it.
      let replacementFunction = this.dslService.getReplacementFunctionForRdslrClause(this.ruleAddModalClause, rowToAdd);
      if (replacementFunction) {
        let replacedClause = replacementFunction(rowToAdd, this.setClauseInputRowWithEmptyValueAndIncrementTheCounter, this.getInputCounter);
        this.ruleClauses[this.ruleAddModalClause].splice(this.rulePosition, 0, replacedClause);
      } else {
        // No changes are needed on this row.
        this.$log.info('INFO: Unable to add row and its validations. No dsl regex was matched for: ' + rowToAdd);
      }
    },

    setClauseInput(key, valueRegexObject) {
      // This is something like this.clauseInputs[key] = valueRegexObject;
      // In order Vuejs to make it reactive (available live v-bind) we need to tell Vuejs that we are adding this property.
      this.$set(this.clauseInputs, key, valueRegexObject);
    },

    removeClauseRow(clause, row) {
      this.ruleClauses[clause].splice(row, 1);
    },
    confirmRuleRemoval() {
      if (!this.newRule || (this.newRule && (JSON.stringify(this.ruleClauses) !== JSON.stringify(this.originalRuleClauses)))) {
        this.showConfirmForRemoveRule = true;
      } else {
        this.$emit('remove-new-rule');
      }
    },
    removeRule() {
      this.showConfirmForRemoveRule = false;
      if (this.newRule) {
        this.$emit('remove-new-rule');
      } else {
        this.removeExistingRule();
      }
    },
    removeExistingRule() {
      this.deleteLoading = true;
      this.rest.deleteRuleFile(this.rule.filename)
        .then(() => {
          this.$emit('refresh-files-list');
          this.deleteLoading = false;
          this.$notify({
            type: 'alert-info',
            text: '<strong>' + this.rule.filename + '</strong> ' + this.$options.filters.translate('SUCCESSFULLY_REMOVED_FILE', 'msg')
          });
        }).catch((err) => {
          this.deleteLoading = false;
          this.$log(err.response.data);
        });
    },
    saveRule() {
      let filename = this.newRule ? this.newRuleFilename
        : this.rule.filename.substring(0, this.rule.filename.indexOf('.rdslr'));
      if (!filename || this.newRuleFilenameIllegal) return;

      let when = '\twhen\n';
      let then = '\tthen\n';
      let end = 'end\n';
      let whenIndex = this.ruleDefaultContent.indexOf(when);
      let thenIndex = this.ruleDefaultContent.indexOf(then);
      let endIndex = this.ruleDefaultContent.indexOf(end);
      let merged = this.ruleDefaultContent.substring(0, whenIndex + when.length).replace('$filename$', filename) +
                  '\t\t' + this.stringifiedRuleClauses['when'].join('\n\t\t') + '\n' +
                  this.ruleDefaultContent.substring(thenIndex, thenIndex + then.length) +
                  '\t\t' + this.stringifiedRuleClauses['then'].join('\n\t\t') + '\n' +
                  this.ruleDefaultContent.substring(endIndex);
      this.saveLoading = true;
      this.rest.saveRuleFile(filename, merged)
        .then(() => {
          let fileToSave = { filename: filename + '.rdslr', content: merged };
          this.$emit('refresh-files-list', fileToSave);
          this.saveLoading = false;
          this.$notify({
            type: 'alert-success',
            text: '<strong>' + fileToSave.filename + '</strong> ' + this.$options.filters.translate('SAVED_SUCCESSFULLY', 'msg')
          });
        }).catch((err) => {
          this.saveLoading = false;
          this.$notify({
            type: 'alert-danger',
            duration: -1,
            text: err.response.data
          });
        });
    },
    showRuleAddModal: function(clause, index) {
      this.ruleAddModalClause = clause;
      this.rulePosition = index + 1; // to be able to add at the top
      this.ruleAddModalShown = true;
    },
    hideRuleAddModal: function() {
      this.ruleAddModalShown = false;
      this.ruleAddModalClause = null;
      this.rulePosition = null;
    },
    setSelectedRuleModalIndex: function(selectedRuleModalIndex) {
      this.lastModalRuleSelectionIndex[this.ruleAddModalClause] = selectedRuleModalIndex;
    }
  },
  watch: {
    originalRuleClauses: function() {
      this.resetRule();
    },
    newRuleFilename(newVal) {
      let re = /[^A-Z0-9_-]/gi;
      this.newRuleFilenameIllegal = (newVal.match(re) && newVal.match(re).length > 0);
    },
    isRuleEnabled(val) {
      let when = '\twhen\n';
      this.ruleDefaultContent = val ? this.ruleDefaultContent.replace(this.enabledFalseOption, '')
          : this.ruleDefaultContent.replace(when, this.enabledFalseOption + when);
    }
  }
}
</script>

<style scoped>
  .file-loading {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    padding-top: 100px;
    text-align: center;
    background-color: rgba(255, 255, 255, 0.5);
    z-index: 1;
  }
  .no-file-container {
    padding: 100px 50px;
    text-align: center;
  }
  .form-control.invalid {
    border-color: #dc3545;
  }
  .form-control.invalid:focus {
    box-shadow: 0 0 0 .2rem rgba(220, 53, 69, .25);
  }
  .table-striped .form-control {
    margin: 0 4px;
    padding-top: 0;
    padding-bottom: 0;
    display: inline-block;
    width: auto;
  }
  .table-striped {
    border-bottom: 1px solid #dee2e6;
  }
  .table-striped tbody {
    counter-reset: rule-counter;
  }
  .table-striped tbody td.with-counter:before {
    counter-increment: rule-counter;
    content: counter(rule-counter)".";
    margin-right: 10px;
    display: inline-block;
    width: 30px;
    text-align: right;
  }
  .action-icon {
    display: inline-block;
    cursor: pointer;
  }
  .action-icon.text-success {
    transform: translateY(16px);
  }
  .filename-alert {
    padding: .55rem .8rem;
    font-size: .8rem;
  }
  .btn.checkbox-inside,
  .btn.checkbox-inside .custom-control {
    padding: 0;
  }
  .btn.checkbox-inside .custom-control-label {
    padding: .25rem .5rem .25rem 2rem;
  }
  .btn.checkbox-inside .custom-control-label:before,
  .btn.checkbox-inside .custom-control-label:after {
    top: .45rem;
    left: .5rem;
  }
</style>



// WEBPACK FOOTER //
// src/components/admin/rules/RulesEditArea.vue